import { z } from 'zod'
import { decimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { commercialOperationTypeSchema } from './pricing.ts'
import { roleSchema } from './roles.ts'
import { unitSchema } from './units.ts'
import { isoCurrencySchema } from './warehouse.ts'

export const PROPOSAL_STATUSES = [
  'draft',
  'review',
  'sent',
  'accepted',
  'rejected',
  'overdue',
  'superseded',
] as const

export const proposalStatusSchema = z.enum(PROPOSAL_STATUSES)
export type ProposalStatus = z.infer<typeof proposalStatusSchema>

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  draft: 'Чернетка',
  review: 'На погодженні',
  sent: 'Надіслано',
  accepted: 'Прийнято',
  rejected: 'Відхилено',
  overdue: 'Прострочено',
  superseded: 'Замінено новою версією',
}

export const createProposalInputSchema = z.object({
  dealId: uuidSchema,
  terms: z.string().trim().max(5000).nullable().default(null),
  validUntil: z.iso.date().nullable().default(null),
})

export type CreateProposalInput = z.infer<typeof createProposalInputSchema>

export const updateProposalStatusInputSchema = z.object({
  status: z.enum(['review', 'sent', 'accepted', 'rejected', 'overdue']),
  comment: z.string().trim().min(1).max(1000).nullable().default(null),
})

export type UpdateProposalStatusInput = z.infer<typeof updateProposalStatusInputSchema>

/** Replace a sent, rejected or overdue revision with the next version from current deal pricing. */
export const createProposalRevisionInputSchema = z.object({
  comment: z.string().trim().min(1).max(1000).nullable().default(null),
  terms: z.string().trim().max(5000).nullable().optional(),
  validUntil: z.iso.date().nullable().optional(),
})

export type CreateProposalRevisionInput = z.infer<typeof createProposalRevisionInputSchema>

export const proposalLineItemSchema = z.object({
  id: uuidSchema,
  revisionId: uuidSchema,
  position: decimalSchema,
  description: z.string().min(1),
  quantity: decimalSchema,
  unit: unitSchema,
  unitPrice: decimalSchema,
  discountPercent: decimalSchema,
  netAmount: decimalSchema,
  vatAmount: decimalSchema,
  totalAmount: decimalSchema,
  createdAt: timestampSchema,
})

export type ProposalLineItem = z.infer<typeof proposalLineItemSchema>

export const proposalRevisionSchema = z.object({
  id: uuidSchema,
  proposalId: uuidSchema,
  version: z.number().int().positive(),
  status: proposalStatusSchema,
  clientName: z.string().min(1),
  clientEdrpou: z.string().nullable(),
  sellerEntityId: uuidSchema.nullable(),
  sellerName: z.string().nullable(),
  sellerEdrpou: z.string().nullable(),
  currencyCode: isoCurrencySchema,
  vatTreatmentCode: z.string().nullable(),
  vatTreatmentName: z.string().nullable(),
  vatRate: decimalSchema.nullable(),
  pricesIncludeVat: z.boolean().nullable(),
  operationType: commercialOperationTypeSchema,
  counterpartyCountryCode: z.string().nullable(),
  requestedRateDate: z.iso.date(),
  effectiveRateDate: z.iso.date(),
  exchangeRate: decimalSchema,
  exchangeRateUnits: decimalSchema,
  exchangeRateProvider: z.string().min(1),
  exchangeRateSourceUrl: z.string().min(1),
  netAmount: decimalSchema,
  vatAmount: decimalSchema,
  totalAmount: decimalSchema,
  uahEquivalent: decimalSchema,
  terms: z.string().nullable(),
  validUntil: z.iso.date().nullable(),
  createdBy: z.string().min(1),
  createdAt: timestampSchema,
})

export type ProposalRevision = z.infer<typeof proposalRevisionSchema>

export const proposalStatusHistoryEntrySchema = z.object({
  id: uuidSchema,
  revisionId: uuidSchema,
  fromStatus: proposalStatusSchema.nullable(),
  toStatus: proposalStatusSchema,
  actorId: z.string().min(1),
  actorRole: roleSchema,
  actorName: z.string().nullable(),
  comment: z.string().nullable(),
  occurredAt: timestampSchema,
})

export type ProposalStatusHistoryEntry = z.infer<typeof proposalStatusHistoryEntrySchema>

export const proposalSchema = z.object({
  id: uuidSchema,
  number: z.string().min(1),
  dealId: uuidSchema,
  clientId: uuidSchema,
  createdBy: z.string().min(1),
  createdAt: timestampSchema,
})

export type Proposal = z.infer<typeof proposalSchema>

export const proposalListItemSchema = proposalSchema.extend({
  dealName: z.string().min(1),
  clientName: z.string().min(1),
  revisionId: uuidSchema,
  version: z.number().int().positive(),
  status: proposalStatusSchema,
  totalAmount: decimalSchema,
  currencyCode: isoCurrencySchema,
  validUntil: z.iso.date().nullable(),
})

export type ProposalListItem = z.infer<typeof proposalListItemSchema>

export const proposalDetailSchema = z.object({
  proposal: proposalSchema,
  dealName: z.string().min(1),
  revision: proposalRevisionSchema,
  lineItems: z.array(proposalLineItemSchema),
  history: z.array(proposalStatusHistoryEntrySchema),
  creatorName: z.string().nullable(),
})

export type ProposalDetail = z.infer<typeof proposalDetailSchema>
